import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaStar, FaCalendarAlt, FaRocket, FaGem, FaHeart, FaBrain, FaUsers, FaChartLine } from 'react-icons/fa';

const Launching: React.FC = () => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [daysUntilLaunch, setDaysUntilLaunch] = useState(0);
  const [hoursUntilLaunch, setHoursUntilLaunch] = useState(0);
  const [minutesUntilLaunch, setMinutesUntilLaunch] = useState(0);
  const [secondsUntilLaunch, setSecondsUntilLaunch] = useState(0);

  const features = [
    {
      icon: <FaGem className="text-4xl text-purple-400" />,
      title: "AI-Powered Astrology",
      description: "Experience personalized astrological insights powered by cutting-edge AI technology that understands your unique cosmic blueprint.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <FaHeart className="text-4xl text-red-400" />,
      title: "Compatibility Matching",
      description: "Discover deep compatibility insights with our advanced matchmaking system that analyzes planetary positions and cosmic energies.",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: <FaBrain className="text-4xl text-blue-400" />,
      title: "Intelligent Conversations",
      description: "Engage in meaningful astrological conversations with our AI that remembers your chart and provides contextual guidance.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <FaUsers className="text-4xl text-green-400" />,
      title: "Group Compatibility",
      description: "Explore how multiple people's energies interact with our revolutionary group compatibility analysis.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <FaChartLine className="text-4xl text-yellow-400" />,
      title: "Life Journey Tracking",
      description: "Track your personal growth and life patterns with our comprehensive astrological journey mapping.",
      color: "from-yellow-500 to-orange-500"
    }
  ];

  useEffect(() => {
    const launchDate = new Date('2025-11-01T00:00:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const difference = launchDate.getTime() - now.getTime();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setDaysUntilLaunch(days);
        setHoursUntilLaunch(hours);
        setMinutesUntilLaunch(minutes);
        setSecondsUntilLaunch(seconds);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen cosmic-bg relative overflow-hidden">
      {/* Animated Background Stars */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 2 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Hero Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-12"
        >
          {/* Section Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 bg-purple-600/30 backdrop-blur-sm rounded-full px-4 py-2 border border-purple-400/50 mb-4">
              <FaStar className="text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">COMING SOON</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent mb-4">
              RRAASI
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-6">
              The Future of Astrological Intelligence
            </p>
          </div>
          
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-6"
          >
            <FaRocket className="text-6xl text-purple-400 mx-auto" />
          </motion.div>
          
          <div className="inline-flex items-center gap-2 bg-purple-900/30 backdrop-blur-sm rounded-full px-6 py-3 border border-purple-400/30">
            <FaCalendarAlt className="text-purple-400" />
            <span className="text-purple-200 font-semibold">Launching November 1st, 2025</span>
          </div>
        </motion.div>

        {/* Countdown Timer */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 backdrop-blur-sm rounded-2xl p-8 mb-12 border border-purple-400/30"
        >
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-purple-600/30 backdrop-blur-sm rounded-full px-4 py-2 border border-purple-400/50 mb-4">
              <FaCalendarAlt className="text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">LAUNCH COUNTDOWN</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Countdown to Launch
            </h2>
            <p className="text-gray-300 text-lg">
              The future of astrological intelligence is almost here
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: daysUntilLaunch, label: 'Days' },
              { value: hoursUntilLaunch, label: 'Hours' },
              { value: minutesUntilLaunch, label: 'Minutes' },
              { value: secondsUntilLaunch, label: 'Seconds' }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="text-center"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity, delay: index * 0.1 }}
              >
                <div className="bg-purple-800/50 rounded-lg p-4 border border-purple-400/30">
                  <div className="text-3xl md:text-4xl font-bold text-purple-200">
                    {item.value.toString().padStart(2, '0')}
                  </div>
                  <div className="text-sm text-purple-300 mt-1">
                    {item.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Feature Showcase */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
          className="mb-12"
        >
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-purple-600/30 backdrop-blur-sm rounded-full px-4 py-2 border border-purple-400/50 mb-4">
              <FaGem className="text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">FEATURES</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              What's Coming
            </h2>
            <p className="text-gray-300 text-lg">
              Revolutionary features that will transform your astrological journey
            </p>
          </div>
          
          <div className="relative h-96">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className={`absolute inset-0 bg-gradient-to-br ${feature.color} rounded-2xl p-8 flex flex-col items-center justify-center text-center ${
                  currentFeature === index ? 'opacity-100' : 'opacity-0'
                }`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: currentFeature === index ? 1 : 0,
                  scale: currentFeature === index ? 1 : 0.8,
                }}
                transition={{ duration: 0.5 }}
              >
                <div className="mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-lg text-white/90 max-w-md">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
          
          {/* Feature Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {features.map((_, index) => (
              <motion.button
                key={index}
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentFeature === index ? 'bg-purple-400' : 'bg-purple-400/30'
                }`}
                onClick={() => setCurrentFeature(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </motion.div>

        {/* Early Access */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/30"
        >
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-purple-600/30 backdrop-blur-sm rounded-full px-4 py-2 border border-purple-400/50 mb-4">
              <FaRocket className="text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">EARLY ACCESS</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Be Among the First to Experience
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              Join our exclusive early access list and be the first to discover how AI and ancient wisdom combine to unlock your cosmic potential.
            </p>
          </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Join Waitlist
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-purple-400 text-purple-200 px-8 py-4 rounded-full font-semibold text-lg hover:bg-purple-400/20 transition-all duration-300"
              >
                Learn More
              </motion.button>
            </div>
        </motion.div>

        {/* Testimonials Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2 }}
          className="mb-12"
        >
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-purple-600/30 backdrop-blur-sm rounded-full px-4 py-2 border border-purple-400/50 mb-4">
              <FaHeart className="text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">TESTIMONIALS</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              What Astrologers Say
            </h2>
            <p className="text-gray-300 text-lg">
              Industry experts share their excitement about RRAASI
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "Dr. Sarah Chen",
                role: "AI Researcher & Astrologer",
                quote: "RRAASI represents the perfect fusion of ancient wisdom and modern technology. This will revolutionize how we understand astrological compatibility."
              },
              {
                name: "Marcus Rodriguez",
                role: "Vedic Astrology Expert",
                quote: "The AI-powered insights are incredibly accurate. It's like having a master astrologer available 24/7."
              },
              {
                name: "Priya Sharma",
                role: "Relationship Counselor",
                quote: "The compatibility matching is extraordinary. It goes beyond surface-level analysis to reveal deep cosmic connections."
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 2.2 + index * 0.1 }}
                className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-sm" />
                  ))}
                </div>
                <p className="text-gray-200 mb-4 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="text-purple-200 font-semibold">{testimonial.name}</p>
                  <p className="text-gray-400 text-sm">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2.5 }}
          className="text-center mt-12 text-gray-400"
        >
          <p className="text-sm">
            © 2025 RRAASI. Revolutionizing astrological intelligence through AI.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Launching; 